/**
 * Root component for the Performance web.
 *
 * What lives here:
 *   - QueryClientProvider (identical settings to apps/web)
 *   - BrowserRouter with FOUR routes:
 *       /login          (own login page — different branding)
 *       /               (own home — 2 tiles)
 *       /report-perf    (shared from apps/web)
 *       /report-compare (shared from apps/web)
 *   - Guard that checks for 'report-perf' / 'report-compare' /
 *     'performance-access' permissions
 *
 * Everything else (TopNav, useAuthBootstrap, ToastHost, auth store)
 * is shared from apps/web via the `@/` Vite alias.
 */
import { lazy, Suspense } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { useAuthStore } from '@/stores/auth';
import { useAuthBootstrap } from '@/hooks/use-auth';
import { TopNav } from '@/components/layout/TopNav';
import { ToastHost } from '@/components/ui/Toast';
import type { MenuKey } from '@ope-v3/shared';

// Own pages (Performance-specific branding)
const LoginPage = lazy(() => import('@perf/routes/login'));
const HomePage = lazy(() => import('@perf/routes/home'));

// Shared pages — re-used directly from apps/web. Any edit there
// automatically reflects here, so we never drift apart.
const ReportPerfPage = lazy(() => import('@/routes/report-perf'));
const ReportComparePage = lazy(() => import('@/routes/report-compare'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      refetchInterval: 30_000,
      refetchIntervalInBackground: false,
      staleTime: 15_000,
      retry: (failureCount, err: unknown) => {
        if (
          err &&
          typeof err === 'object' &&
          'status' in err &&
          (err as { status: number }).status === 401
        ) {
          return false;
        }
        return failureCount < 2;
      },
    },
  },
});

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Shell />
        <ToastHost />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

function Shell(): JSX.Element {
  useAuthBootstrap();
  const initialized = useAuthStore((s) => s.initialized);
  const user = useAuthStore((s) => s.user);

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-500">
        ⏳ กำลังตรวจสอบ session...
      </div>
    );
  }

  return (
    <>
      <TopNav />
      <Suspense
        fallback={
          <div className="min-h-screen flex items-center justify-center text-gray-500">
            ⏳ กำลังโหลด...
          </div>
        }
      >
        <Routes>
          <Route
            path="/login"
            element={user ? <Navigate to="/" replace /> : <LoginPage />}
          />
          <Route
            path="/"
            element={
              <Guard>
                <HomePage />
              </Guard>
            }
          />
          <Route
            path="/report-perf"
            element={
              <Guard menu="report-perf">
                <ReportPerfPage />
              </Guard>
            }
          />
          <Route
            path="/report-compare"
            element={
              <Guard menu="report-compare">
                <ReportComparePage />
              </Guard>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

function Guard({
  children,
  menu,
}: {
  children: JSX.Element;
  menu?: MenuKey;
}): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const canAccess = useAuthStore((s) => s.canAccess);
  const location = useLocation();

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Gate ALL pages in this app behind 'performance-access' (or admin).
  // Admin bypasses; otherwise the user must have this permission.
  if (!user.isAdmin && !canAccess('performance-access')) {
    return (
      <div className="max-w-md mx-auto mt-20 p-6 bg-white rounded-2xl shadow text-center">
        <div className="text-4xl mb-3">🔒</div>
        <h2 className="text-lg font-semibold text-gray-800 mb-2">
          ไม่มีสิทธิ์เข้าใช้งาน
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          ระบบรายงานผลการปฏิบัติงาน — กรุณาติดต่อ Admin
          เพื่อเพิ่มสิทธิ์ &quot;performance-access&quot;
        </p>
        <a
          href="https://ope-system-v3.pages.dev"
          className="text-blue-600 hover:underline text-sm"
        >
          ← กลับเว็บหลัก (OPE)
        </a>
      </div>
    );
  }

  // Page-specific permission (additional layer for individual reports).
  if (menu && !canAccess(menu)) {
    return (
      <div className="max-w-md mx-auto mt-20 p-6 bg-white rounded-2xl shadow text-center">
        <div className="text-4xl mb-3">🔒</div>
        <h2 className="text-lg font-semibold text-gray-800 mb-2">
          ไม่มีสิทธิ์เข้าถึง
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          กรุณาติดต่อผู้ดูแลระบบเพื่อขอสิทธิ์ใช้งานเมนูนี้
        </p>
        <a href="/" className="text-blue-600 hover:underline text-sm">
          ← กลับหน้าหลัก
        </a>
      </div>
    );
  }

  return children;
}
